import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface AuthSnapshot {
  token: string;
  userId: string;
  tenantId: string;
  email: string;
}

interface AuthState {
  token: string | null;
  userId: string | null;
  tenantId: string | null;
  email: string | null;
  role: string | null; // 'admin' | 'user' on the active account, null while loading
  setAuth: (snapshot: AuthSnapshot) => void;
  setRole: (role: string | null) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      userId: null,
      tenantId: null,
      email: null,
      role: null,
      setAuth: (s) => {
        set({ token: s.token, userId: s.userId, tenantId: s.tenantId, email: s.email });
        // Notify the extension web-bridge so it can sync the token without a separate login.
        try {
          window.dispatchEvent(new CustomEvent('trailmark:auth-changed', { detail: s }));
        } catch { /* no window in SSR */ }
      },
      setRole: (role) => set({ role }),
      clearAuth: () => {
        set({ token: null, userId: null, tenantId: null, email: null, role: null });
        try {
          window.dispatchEvent(new CustomEvent('trailmark:auth-cleared'));
        } catch { /* no window in SSR */ }
      },
    }),
    {
      name: 'trailmark.auth',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const isAuthenticated = (): boolean => useAuthStore.getState().token !== null;

/**
 * Call once at app root. Listens for localStorage writes from other tabs and
 * rehydrates the store so login/logout in one tab is reflected everywhere.
 * Returns a cleanup function for useEffect.
 */
export function syncAuthAcrossTabs(): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === 'trailmark.auth') {
      useAuthStore.persist.rehydrate();
    }
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
